import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import './Hero.css';

gsap.registerPlugin(ScrollTrigger, TextPlugin);

const roles = ['Full‑Stack Developer', 'React Enthusiast', 'API Builder', 'Problem Solver'];

function Hero() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const roleRef = useRef(null);
  const descRef = useRef(null);
  const buttonsRef = useRef(null);
  const scrollRef = useRef(null);
  const orb1Ref = useRef(null);
  const orb2Ref = useRef(null);
  const orb3Ref = useRef(null);

  useEffect(() => {
    let roleIdx = 0;
    let roleTimeout;

    const cycleRole = () => {
      roleIdx = (roleIdx + 1) % roles.length;
      gsap.to(roleRef.current, {
        duration: 0.3,
        opacity: 0,
        y: -10,
        onComplete: () => {
          if (roleRef.current) {
            roleRef.current.textContent = roles[roleIdx];
          }
          gsap.to(roleRef.current, { duration: 0.4, opacity: 1, y: 0 });
        },
      });
      roleTimeout = setTimeout(cycleRole, 2500);
    };

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.3 });

      // Heading
      tl.fromTo(headingRef.current,
        { opacity: 0, y: 60, clipPath: 'inset(100% 0 0 0)' },
        { opacity: 1, y: 0, clipPath: 'inset(0% 0 0 0)', duration: 1, ease: 'power4.out' },
        '-=0.3'
      );

      // Role
      tl.fromTo(roleRef.current,
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.7, ease: 'power3.out' },
        '-=0.5'
      );

      // Description
      tl.fromTo(descRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' },
        '-=0.4'
      );

      // Buttons
      tl.fromTo(buttonsRef.current.children,
        { opacity: 0, y: 30, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, stagger: 0.12, ease: 'back.out(1.5)' },
        '-=0.3'
      );

      // Scroll indicator
      tl.fromTo(scrollRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.5 },
        '-=0.1'
      );

      // Orb floating animations
      gsap.to(orb1Ref.current, {
        y: -40,
        x: 20,
        duration: 6,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
      });
      gsap.to(orb2Ref.current, {
        y: 30,
        x: -20,
        duration: 7,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: 1,
      });
      gsap.to(orb3Ref.current, {
        y: -25,
        x: -15,
        duration: 5,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: 2,
      });

      // Scroll indicator bounce
      gsap.to('.scroll-arrow', {
        y: 8,
        duration: 1.2,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
      });

      // Parallax on scroll
      gsap.to(sectionRef.current, {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
        y: 100,
        opacity: 0.5,
      });
    });

    // Start role cycling after initial animation
    roleTimeout = setTimeout(cycleRole, 3000);

    return () => {
      ctx.revert();
      clearTimeout(roleTimeout);
    };
  }, []);

  const handleScrollDown = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="hero" ref={sectionRef}>
      {/* Ambient orbs */}
      <div ref={orb1Ref} className="hero-orb hero-orb-1" />
      <div ref={orb2Ref} className="hero-orb hero-orb-2" />
      <div ref={orb3Ref} className="hero-orb hero-orb-3" />

      {/* Grid overlay */}
      <div className="hero-grid" />

      <div className="hero-content">
        {/* Heading */}
        <h1 ref={headingRef} className="hero-heading">
          Hi, I'm{' '}
          <span className="hero-name">
            Pratik Patil
            <span className="hero-name-underline" />
          </span>
        </h1>

        {/* Role */}
        <div className="hero-role-wrapper">
          <span className="hero-role-prefix">&gt;_</span>
          <span ref={roleRef} className="hero-role">{roles[0]}</span>
          <span className="hero-cursor">|</span>
        </div>

        {/* Description */}
        <p ref={descRef} className="hero-desc">
          I build fast, real, working systems — from clean APIs to responsive UIs.
          Passionate about crafting digital experiences that just{' '}
          <em className="hero-em">work beautifully</em>.
        </p>

        {/* Buttons */}
        <div ref={buttonsRef} className="hero-buttons">
          <button className="btn-primary" onClick={handleScrollDown}>
            <span>Explore My Work</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
          <a href="#contact" className="btn-outline" onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}>
            Get In Touch
          </a>
        </div>

        {/* Stats row */}
        <div className="hero-stats">
          <div className="hero-stat">
            <span className="stat-val">20+</span>
            <span className="stat-key">Projects</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <span className="stat-val">1+</span>
            <span className="stat-key">Years Exp</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <span className="stat-val">∞</span>
            <span className="stat-key">Curiosity</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div ref={scrollRef} className="scroll-indicator" onClick={handleScrollDown}>
        <span className="scroll-label">Scroll down</span>
        <div className="scroll-arrow">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </div>
      </div>
    </section>
  );
}

export default Hero;
